// src/components/cognition/cognition-analysis.tsx
//
// Route root for the AI Reasoning Path (Cognition) module. This is the container
// (mirrors deep-signal-page.tsx): it owns ALL state, the websocket run
// lifecycle, history, config persistence, and the fix-actions integration, then
// composes the restyled sections. CognitionPageHeader is left untouched
// (separate platform-wide header pass). Prop contract is unchanged.

import { useEffect, useMemo, useRef, useState, useCallback } from "react";

import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { CommandPalette, useCommandPalette } from "../command-palette";
import { cognitionCommands } from "../../data/cognition-metric-explanations";
import { toast } from "sonner";

import { useCognitionSetup } from "@/app/hooks/cognition/useCognitionSetup";
import { openCognitionSocket, type CognitionWsEvent } from "@/app/services/cognition/cognition-ws";
import { fixActionsService } from "@/app/services/fix-actions/fix-actions-service";
import { AVAILABLE_AI_MODELS, normalizeProviderKey } from "@/app/constants/models";
import { buildWsUrl } from "@/api/url-route";
import { getStoredToken, notifyAuthExpired } from "@/api/api-fetch";
import {
  fetchCognitionHistory,
  fetchCognitionRun,
  type CognitionHistoryItem,
} from "@/app/services/cognition/cognition-service";
import {
  displayModel,
  safeCognitionConfig,
  isConfigChanged,
  type CognitionConfigState,
} from "@/app/utils/brand-helpers";

import { CognitionPageHeader } from "./cognition-page-header";
import { CognitionModelModal } from "./cognition-model-modal";
import { LockedFeatureModal } from "../navigation/locked-feature-modal";
import { CognitionCtx } from "./context";
import { TabsBar } from "./components/tabs-bar";
import { SetupSection } from "./sections/setup-section";
import { RunSummarySection } from "./sections/run-summary-section";
import { PipelineSection } from "./sections/pipeline-section";
import { GapsSection } from "./sections/gaps-section";
import { HistorySection } from "./sections/history-section";
import {
  buildCandidates,
  buildModelPills,
  buildStages,
  buildPreviewStages,
  buildRunSummary,
  buildOptimizations,
  buildGaps,
  buildHistory,
  gapRowId,
} from "./builders";
import type { TabId, ActionCardStatus, OptimizationVM, OptimizationTarget, StageId } from "./types";

interface CognitionAnalysisProps {
  brandName: string;
  brandId?: string;
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  onNavigate?: (page: string) => void;
  onNavigateToFixActions?: (actionTitle: string) => void;

  cognitionData: any | null;
  onCognitionData: (data: any | null) => void;

  cognitionConfig: CognitionConfigState;
  onCognitionConfigChange: (cfg: CognitionConfigState) => void;
}

const STAGE_DISPLAY_NAME: Record<string, string> = {
  "task-setup": "Stage 1: Task & Setup",
  "evidence-signals": "Stage 2: Evidence & Signals",
  "understanding-decisions": "Stage 3: Competitive Comparison",
  "final-synthesis": "Stage 4: Response Construction",
};

// ── Action Items gate (same temporary pattern as Brand Monitoring) ──────────
// While false, the optimization card's Add / View buttons open a coming-soon
// modal instead of writing to / navigating into the not-yet-available Action
// Items module. Flip to true to restore the real fix-actions behavior.
const ACTION_ITEMS_ENABLED = true;
const ACTION_ITEMS_LOCK_COPY = {
  title: "Action Items is almost here",
  desc:
    "This is where a recommendation becomes a tracked task: see exactly what to change and where, mark it done, and watch your score respond. We're putting the finishing touches on it now.",
  highlights: [
    "Send any recommendation straight to your task list",
    "Step-by-step fix instructions with sources",
    "Track what's shipped and what's still open",
  ],
  cta: "Got it",
  toast: "Action Items is still in the works. It will unlock here automatically when it ships.",
};

export function CognitionAnalysis({
  brandName,
  brandId,
  isSidebarCollapsed = false,
  onSidebarToggle,
  onNavigate,
  onNavigateToFixActions,
  cognitionData,
  onCognitionData,
  cognitionConfig,
  onCognitionConfigChange,
}: CognitionAnalysisProps) {
  const { isOpen, closeCommandPalette } = useCommandPalette();
  const [activeTab, setActiveTab] = useState<TabId>("analysis");

  const effectiveBrandId = useMemo(() => {
    try {
      const persisted = JSON.parse(localStorage.getItem("bm_last_result") || "{}");
      return String(brandId || persisted?.runId || localStorage.getItem("bm_brand_id") || "default");
    } catch {
      return String(brandId || localStorage.getItem("bm_brand_id") || "default");
    }
  }, [brandId]);

  const {
    products: autoExtractedProducts,
    brandSnapshot: _brandSnapshot,
    loading: candidatesLoading,
    error: candidatesError,
  } = useCognitionSetup(brandName);

  const [history, setHistory] = useState<CognitionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRefreshNonce, setHistoryRefreshNonce] = useState(0);

  const [runResult, setRunResult] = useState<any | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const aiModels = AVAILABLE_AI_MODELS;
  const recommendedModelIds = useMemo(
    () => aiModels.filter((m) => m.recommended).map((m) => m.id),
    [aiModels]
  );

  // Persisted config state
  const [isConfigMinimized, setIsConfigMinimized] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [showModelModal, setShowModelModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [productSpecRaw, setProductSpecRaw] = useState<string>("");
  const [specsOpen, setSpecsOpen] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Action Items coming-soon modal + gap deep-link state
  const [showActionLock, setShowActionLock] = useState(false);
  const [gapsExpandNonce, setGapsExpandNonce] = useState(0);
  const [gapsHighlightAttr, setGapsHighlightAttr] = useState<string | null>(null);

  // Run state
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  // Non-fatal per-model failures (e.g. Gemini timing out / returning no JSON).
  // Keyed by engine id so a later final_result can overwrite/confirm.
  const [modelErrors, setModelErrors] = useState<Record<string, string>>({});
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  // AI sidebar wiring
  const [aiContextualUpdate] = useState<
    { title: string; description: string; timestamp?: number } | undefined
  >(undefined);
  const [externalQuestion, setExternalQuestion] = useState<{ text: string; ts: number } | undefined>(undefined);

  // Streaming pipeline state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentLoadingStage, setCurrentLoadingStage] = useState<string | null>(null);
  const [stageLoadingMessage, setStageLoadingMessage] = useState<string>("");
  const [completedStages, setCompletedStages] = useState<Set<string>>(new Set());

  const resetPipelineLoading = () => {
    setIsAnalyzing(false);
    setCurrentLoadingStage(null);
    setStageLoadingMessage("");
    setCompletedStages(new Set());
  };

  const STAGE_BY_PROVIDER: Record<string, string> = {
    openai: "task-setup",
    claude: "evidence-signals",
    perplexity: "understanding-decisions",
  };
  const stageForModel = (engine: string) => STAGE_BY_PROVIDER[normalizeProviderKey(engine)] ?? "final-synthesis";
  const stageForProgress = (percent: number) => {
    const p = Math.max(0, Math.min(100, Number(percent) || 0));
    if (p < 25) return "task-setup";
    if (p < 50) return "evidence-signals";
    if (p < 75) return "understanding-decisions";
    return "final-synthesis";
  };
  const stageStartCopy = (stageId: string) => {
    switch (stageId) {
      case "task-setup": return "Analyzing how AI models frame your search...";
      case "evidence-signals": return "Scanning information sources across models...";
      case "understanding-decisions": return "Comparing competitive rankings...";
      case "final-synthesis": return "Cross-checking consistency across models...";
      default: return "Analyzing...";
    }
  };

  // 1) Hydrate once
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (didHydrateRef.current) return;
    didHydrateRef.current = true;
    const cfg = safeCognitionConfig(cognitionConfig);
    setIsConfigMinimized(cfg.isConfigMinimized);
    setProductQuery(cfg.productQuery);
    setSelectedProductName(cfg.selectedProductName);
    setSelectedModels(cfg.selectedModels);
    const restoredFile =
      typeof File !== "undefined" && cfg.uploadedFile instanceof File ? cfg.uploadedFile : null;
    setUploadedFile(restoredFile);
    setProductSpecRaw(cfg.productSpecRaw);
    if (cognitionData) {
      setRunResult(cognitionData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cognitionData) return;
    setRunResult(cognitionData);
  }, [cognitionData]);

  // 2) Init recommended models (if empty)
  const didInitRecommendedRef = useRef(false);
  useEffect(() => {
    if (didInitRecommendedRef.current) return;
    const cfg = safeCognitionConfig(cognitionConfig);
    if (cfg.selectedModels.length > 0 || selectedModels.length > 0) {
      didInitRecommendedRef.current = true;
      return;
    }
    setSelectedModels(recommendedModelIds);
    didInitRecommendedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedModelIds]);

  // 3) Persist config on changes
  const lastSentRef = useRef<CognitionConfigState | null>(null);
  const skipFirstPersistRef = useRef(false);
  useEffect(() => {
    const next: CognitionConfigState = {
      productQuery,
      selectedProductName,
      selectedModels,
      uploadedFile,
      productSpecRaw,
      isConfigMinimized,
    };
    if (!skipFirstPersistRef.current) {
      skipFirstPersistRef.current = true;
      lastSentRef.current = next;
      return;
    }
    if (lastSentRef.current && !isConfigChanged(lastSentRef.current, next)) return;
    lastSentRef.current = next;
    onCognitionConfigChange(next);
  }, [productQuery, selectedProductName, selectedModels, uploadedFile, productSpecRaw, isConfigMinimized, onCognitionConfigChange]);

  // 4) Reset on brand change
  const prevBrandRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevBrandRef.current === null) {
      prevBrandRef.current = brandName;
      return;
    }
    if (prevBrandRef.current === brandName) return;
    prevBrandRef.current = brandName;
    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;
    setSelectedProductName(null);
    setProductQuery("");
    setUploadedFile(null);
    setProductSpecRaw("");
    setRunResult(null);
    setRunError(null);
    setRunLoading(false);
    setExpandedStage(null);
    setShowModelModal(false);
    setShowAllProducts(false);
    resetPipelineLoading();
    onCognitionData(null);
    onCognitionConfigChange({
      productQuery: "",
      selectedProductName: null,
      selectedModels: recommendedModelIds,
      uploadedFile: null,
      productSpecRaw: "",
      isConfigMinimized: false,
    });
    setSelectedModels(recommendedModelIds);
  }, [brandName, onCognitionData, onCognitionConfigChange, recommendedModelIds]);

  useEffect(() => {
    return () => {
      try { wsRef.current?.close(); } catch {}
      wsRef.current = null;
    };
  }, []);

  // History
  useEffect(() => {
    if (!brandName) return;
    let isMounted = true;
    async function loadHistory() {
      setHistoryLoading(true);
      try {
        const items = await fetchCognitionHistory(brandName);
        if (isMounted) setHistory(items);
      } catch (e) {
        console.error("Failed to load history", e);
      } finally {
        if (isMounted) setHistoryLoading(false);
      }
    }
    loadHistory();
    return () => { isMounted = false; };
  }, [brandName, historyRefreshNonce]);

  const handleLoadHistoryRun = async (runId: number) => {
    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;
    resetPipelineLoading();
    setRunLoading(true);
    setRunError(null);
    setActiveTab("analysis");
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const data = await fetchCognitionRun(runId);
      setRunResult(data);
      onCognitionData(data);
      if (data.productName) {
        setSelectedProductName(data.productName);
        setProductQuery(data.productName);
      }
      if (data.selectedModels) setSelectedModels(data.selectedModels);
    } catch (e) {
      setRunError("Failed to load historical run.");
    } finally {
      setRunLoading(false);
    }
  };

  const selectedModelIds: string[] = useMemo(() => {
    const fromRun = runResult?.selectedModels;
    if (Array.isArray(fromRun) && fromRun.length) return fromRun;
    return selectedModels;
  }, [runResult, selectedModels]);

  // ── derived lifecycle ──
  const hasRunData = !!(
    runResult &&
    ((Array.isArray(runResult.cognitiveStages) && runResult.cognitiveStages.length > 0) ||
      runResult.baselineSummaryData)
  );
  const isStreaming = isAnalyzing;
  const isComplete = hasRunData && !isAnalyzing;
  const isPreview = !hasRunData && !isAnalyzing;
  const phase = isStreaming ? "streaming" : isComplete ? "complete" : "setup";

  // ── config handlers ──
  const handleProductPick = (name: string) => {
    setSelectedProductName(name);
    setProductQuery(name);
  };
  const handleChangeProductQuery = (v: string) => {
    setProductQuery(v);
    const typed = String(v || "").trim();
    const picked = String(selectedProductName || "").trim();
    if (selectedProductName && typed && typed !== picked) setSelectedProductName(null);
    if (!typed) setSelectedProductName(null);
  };
  const toggleModel = (modelId: string) =>
    setSelectedModels((prev) => (prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]));
  const useRecommended = () => setSelectedModels(recommendedModelIds);
  const selectAllModels = () => setSelectedModels(aiModels.map((m) => m.id));
  const clearAllModels = () => setSelectedModels([]);

  const readFileAsText = async (file: File): Promise<string> => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (ext === ".json" || ext === ".txt" || ext === ".csv") return await file.text();
    return "";
  };
  const handleFileUpload = async (file: File) => {
    const validTypes = [".pdf", ".docx", ".txt", ".json", ".csv"];
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!validTypes.includes(ext)) return;
    setUploadedFile(file);
    try {
      setProductSpecRaw((await readFileAsText(file)) || "");
    } catch {
      setProductSpecRaw("");
    }
  };
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setProductSpecRaw("");
  };
  const toggleStage = (stageId: string) =>
    setExpandedStage((cur) => (cur === stageId ? null : stageId));

  const handleAsk = (question: string) => {
    onSidebarToggle?.(false);
    setExternalQuestion({ text: question, ts: Date.now() });
  };

  const runCognition = async () => {
    if (!brandName?.trim()) return;
    const effectiveProduct = (productQuery || selectedProductName || "").trim();
    if (!effectiveProduct) return;
    if (!selectedModels.length) return;

    setRunLoading(true);
    setRunError(null);
    setRunResult(null);
    setExpandedStage(null);
    setIsAnalyzing(true);
    setCompletedStages(new Set());
    setCurrentLoadingStage("task-setup");
    setStageLoadingMessage(stageStartCopy("task-setup"));

    const token = getStoredToken();
    const baseWsUrl = buildWsUrl();
    if (!token) {
      setRunLoading(false);
      setRunError("Missing access token. Please login again.");
      resetPipelineLoading();
      notifyAuthExpired("/cognition/ws/cognition-trace");
      return;
    }

    const payload = {
      brandName,
      productName: effectiveProduct,
      models: selectedModels,
      productQuery: (productQuery || effectiveProduct).trim(),
      productSpecRaw: productSpecRaw || "",
    };

    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;

    let ws: WebSocket;
    try {
      ws = openCognitionSocket({
        baseWsUrl,
        token,
        payload,
        onEvent: (evt: CognitionWsEvent) => {
          if (evt.type === "analysis_started") {
            setIsAnalyzing(true);
            setModelErrors({});
            setCompletedStages(new Set());
            setCurrentLoadingStage("task-setup");
            setStageLoadingMessage(stageStartCopy("task-setup"));
            return;
          }
          if (evt.type === "progress") {
            const stageId = stageForProgress(Number((evt as any).percent ?? 0));
            setCurrentLoadingStage(stageId);
            setStageLoadingMessage(stageStartCopy(stageId));
            return;
          }
          if (evt.type === "model_status") {
            const engine = String((evt as any).engine || "");
            const status = (evt as any).status as "queued" | "running" | "done" | "error";
            if (!engine) return;
            const stageId = stageForModel(engine);
            if (status === "queued") return;
            if (status === "running") {
              setCurrentLoadingStage(stageId);
              setStageLoadingMessage(stageStartCopy(stageId));
              return;
            }
            if (status === "error") {
              const msg = String((evt as any).message || "").trim() || "Model failed (no detail).";
              setModelErrors((prev) => ({ ...prev, [engine]: msg }));
            }
            if (status === "done" || status === "error") {
              setCompletedStages((prev) => new Set([...Array.from(prev), stageId]));
              return;
            }
          }
          if (evt.type === "final_result") {
            const errs = (evt as any).errors as Record<string, string> | undefined;
            if (errs && typeof errs === "object") {
              setModelErrors((prev) => ({ ...prev, ...errs }));
            }
            setRunResult(evt.data);
            onCognitionData(evt.data);
            setHistoryRefreshNonce((n) => n + 1);
            setRunLoading(false);
            setCompletedStages(new Set(["task-setup", "evidence-signals", "understanding-decisions", "final-synthesis"]));
            setCurrentLoadingStage(null);
            setStageLoadingMessage("");
            setIsAnalyzing(false);
            setExpandedStage("task-setup");
            return;
          }
          if (evt.type === "error") {
            setRunError(evt.message || "Cognition run failed.");
            setRunLoading(false);
            resetPipelineLoading();
            return;
          }
        },
        onError: () => {
          setRunError("WebSocket error.");
          setRunLoading(false);
          resetPipelineLoading();
        },
      });
    } catch (e) {
      setRunError("Failed to start cognition run.");
      setRunLoading(false);
      resetPipelineLoading();
      return;
    }
    wsRef.current = ws;
  };

  // ── fix-actions integration (ported from the old run-output) ──
  const productId: string = String(runResult?.productId || "");
  const runId: number | undefined = runResult?.runId;
  const totalModels = Math.max(1, Number(selectedModelIds?.length || 1));

  const optimizations = useMemo(
    () => buildOptimizations(runResult, totalModels),
    [runResult, totalModels]
  );

  const buildSourceId = useCallback(
    (cardId: string) => (productId ? `${productId}-${cardId}` : cardId),
    [productId]
  );

  const [cardStatus, setCardStatus] = useState<Record<string, ActionCardStatus>>({});

  const refreshCardStatuses = useCallback(async () => {
    // Gated: don't fetch link status; every card stays an "Add to Action Log"
    // button that opens the coming-soon modal.
    if (!ACTION_ITEMS_ENABLED) {
      setCardStatus({});
      return;
    }
    if (!optimizations.length || !hasRunData) {
      setCardStatus({});
      return;
    }
    const sourceIds = optimizations.map((c) => buildSourceId(c.id));
    try {
      const res = await fixActionsService.getActionsBySource({
        brandId: effectiveBrandId,
        sourceModule: "cognition",
        sourceIds,
      });
      const next: Record<string, ActionCardStatus> = {};
      for (const card of optimizations) {
        const key = buildSourceId(card.id);
        const hit = res?.byId?.[key];
        next[card.id] = hit
          ? {
              linked: true,
              actionId: hit.id,
              actionTitle: hit.title,
              eventStatus: (hit.eventStatus as any) || "Open",
              totalArtifacts: hit.totalArtifacts ?? 0,
              generatedCount: hit.generatedCount ?? 0,
              closedCount: hit.closedCount ?? 0,
            }
          : { linked: false };
      }
      setCardStatus(next);
    } catch (e) {
      console.warn("getActionsBySource failed; rendering all cards as not-linked", e);
      const next: Record<string, ActionCardStatus> = {};
      for (const card of optimizations) next[card.id] = { linked: false };
      setCardStatus(next);
    }
  }, [optimizations, buildSourceId, effectiveBrandId, hasRunData]);

  useEffect(() => {
    if (!hasRunData) return;
    refreshCardStatuses();
  }, [hasRunData, refreshCardStatuses]);

  useEffect(() => {
    const handler = () => refreshCardStatuses();
    window.addEventListener("artifact_published", handler);
    window.addEventListener("event_status_updated", handler);
    window.addEventListener("fix_actions_refresh", handler);
    return () => {
      window.removeEventListener("artifact_published", handler);
      window.removeEventListener("event_status_updated", handler);
      window.removeEventListener("fix_actions_refresh", handler);
    };
  }, [refreshCardStatuses]);

  // "See how we found this" deep-links to where the insight actually came from:
  // a pipeline stage (expand + scroll) or the exact gap row (open gaps, highlight,
  // scroll). Nothing here is hardcoded; targets are derived per optimization.
  const handleNavigate = (target: OptimizationTarget) => {
    if (target.kind === "stage") {
      setExpandedStage(target.stageId);
      setTimeout(() => {
        document.getElementById(`stage-${target.stageId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    // gap target: open the gaps section, highlight + scroll to its row
    setGapsExpandNonce((n) => n + 1);
    setGapsHighlightAttr(target.attribute);
    setTimeout(() => {
      document.getElementById(gapRowId(target.attribute))?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    window.setTimeout(() => setGapsHighlightAttr(null), 2600);
  };

  const handleAddOptimization = async (opt: OptimizationVM) => {
    // Gated: open the coming-soon modal instead of writing to Action Items.
    if (!ACTION_ITEMS_ENABLED) {
      setShowActionLock(true);
      return;
    }
    const targetStageId: StageId =
      opt.target.kind === "stage" ? opt.target.stageId : "evidence-signals";
    setCardStatus((prev) => ({ ...prev, [opt.id]: { ...(prev[opt.id] || { linked: false }), adding: true } }));
    try {
      const resp = await fixActionsService.ingestActionLog({
        brandId: effectiveBrandId,
        sourceModule: "cognition",
        sourceId: buildSourceId(opt.id),
        title: opt.headline,
        description: opt.desc,
        priority: opt.type,
        stageId: targetStageId,
        autoEnrich: true,
        metadata: {
          from: "cognition_priority_optimizations",
          stageName: STAGE_DISPLAY_NAME[targetStageId] || opt.chipLabel,
          productId,
          productName: selectedProductName,
          runId: runId ?? null,
        },
      });
      const totalArtifacts = resp?.action?.artifacts?.length ?? 0;
      setCardStatus((prev) => ({
        ...prev,
        [opt.id]: {
          linked: true,
          actionId: resp?.action?.id,
          actionTitle: resp?.action?.title || opt.headline,
          eventStatus: "Open",
          totalArtifacts,
          generatedCount: 0,
          closedCount: 0,
          adding: false,
        },
      }));
      if (resp?.deduped) {
        toast.success("Already in Action Log");
      } else {
        toast.success(
          totalArtifacts > 0
            ? `Added with ${totalArtifacts} artifact slot${totalArtifacts === 1 ? "" : "s"} ready`
            : "Added to Action Log"
        );
      }
      window.dispatchEvent(new Event("fix_actions_refresh"));
    } catch (e) {
      console.error(e);
      toast.error("Failed to add optimization");
      setCardStatus((prev) => ({ ...prev, [opt.id]: { ...(prev[opt.id] || { linked: false }), adding: false } }));
    }
  };

  const handleViewInFixActions = (opt: OptimizationVM) => {
    if (!ACTION_ITEMS_ENABLED) {
      setShowActionLock(true);
      return;
    }
    const status = cardStatus[opt.id];
    if (!status?.linked) return;
    const title = status.actionTitle || opt.headline;
    if (onNavigateToFixActions) {
      onNavigateToFixActions(title);
      return;
    }
    try { sessionStorage.setItem("fix_actions_highlight_title", title); } catch {}
    if (onNavigate) onNavigate("fix-actions");
    else toast.info("Fix Actions navigation is not wired up.");
  };

  // ── view models ──
  const allCandidates = useMemo(
    () => buildCandidates(autoExtractedProducts || [], selectedProductName),
    [autoExtractedProducts, selectedProductName]
  );
  const shownCandidates = showAllProducts ? allCandidates : allCandidates.slice(0, 4);
  const modelPills = useMemo(() => buildModelPills(selectedModels, displayModel), [selectedModels]);
  const stages = useMemo(
    () => (isComplete ? buildStages(runResult, selectedModelIds, displayModel) : buildPreviewStages()),
    [isComplete, runResult, selectedModelIds]
  );
  const summary = useMemo(() => buildRunSummary(runResult, totalModels), [runResult, totalModels]);
  const gaps = useMemo(() => buildGaps(runResult?.gapsData), [runResult]);
  const historyRows = useMemo(() => buildHistory(history), [history]);

  const canRun =
    !!brandName?.trim() && !!(productQuery || selectedProductName || "").trim() && selectedModels.length > 0;

  const statusFor = (id: string): ActionCardStatus => cardStatus[id] || { linked: false };

  const ctxValue = useMemo(
    () => ({ runResult, phase: phase as any, isPreview, isStreaming, isComplete, modelLabel: displayModel }),
    [runResult, phase, isPreview, isStreaming, isComplete]
  );

  const contentMarginClass = isSidebarCollapsed ? "lg:mr-12" : "lg:mr-[380px]";

  return (
    <CognitionCtx.Provider value={ctxValue}>
      <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <CommandPalette isOpen={isOpen} onClose={closeCommandPalette} commands={cognitionCommands} />

        <div className={`flex-1 overflow-auto transition-all duration-300 ${contentMarginClass}`}>
          {/* Header is untouched (separate platform-wide pass) */}
          <CognitionPageHeader />

          <TabsBar
            tabs={[
              { value: "analysis", label: "Map AI Reasoning Path" },
              { value: "history", label: `History${historyLoading ? "…" : ""}` },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === "analysis" ? (
            <div style={{ padding: "26px clamp(16px, 5vw, 64px) 56px" }}>
              <SetupSection
                brandName={brandName}
                productQuery={productQuery}
                onChangeProductQuery={handleChangeProductQuery}
                selectedProductName={selectedProductName}
                onSelectProductName={handleProductPick}
                candidates={shownCandidates}
                totalProducts={allCandidates.length}
                candidatesLoading={candidatesLoading}
                modelPills={modelPills}
                recommendedCount={selectedModels.length}
                onRemoveModel={toggleModel}
                onUseRecommended={useRecommended}
                onOpenModelModal={() => setShowModelModal(true)}
                onViewAllProducts={() => setShowAllProducts(true)}
                specsOpen={specsOpen}
                onToggleSpecs={() => setSpecsOpen((v) => !v)}
                uploadedFile={uploadedFile}
                onUploadFile={handleFileUpload}
                onRemoveFile={handleRemoveFile}
                onRun={runCognition}
                canRun={canRun}
                runLoading={runLoading}
              />

              {candidatesError && !candidatesLoading && (
                <div
                  style={{
                    margin: "0 0 22px",
                    padding: "12px 16px",
                    borderRadius: 10,
                    background: "var(--infrared-coral-10)",
                    border: "1px solid var(--infrared-coral-20)",
                    color: "var(--infrared-coral)",
                    fontSize: 13,
                  }}
                >
                  Could not load product candidates. {candidatesError?.message || "Please try again."}
                </div>
              )}

              {runError && (
                <div
                  style={{
                    margin: "0 0 22px",
                    padding: "12px 16px",
                    borderRadius: 10,
                    background: "var(--infrared-coral-10)",
                    border: "1px solid var(--infrared-coral-20)",
                    color: "var(--infrared-coral)",
                    fontSize: 13,
                  }}
                >
                  {runError}
                </div>
              )}

              {Object.keys(modelErrors).length > 0 && (
                <div
                  style={{
                    margin: "0 0 22px",
                    padding: "12px 16px",
                    borderRadius: 10,
                    background: "var(--warning-bg, var(--infrared-coral-10))",
                    border: "1px solid var(--warning-text, var(--infrared-coral-20))",
                    color: "var(--warning-text, var(--infrared-coral))",
                    fontSize: 13,
                  }}
                >
                  <strong style={{ display: "block", marginBottom: 6 }}>
                    Some models returned no data and were left blank:
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {Object.entries(modelErrors).map(([engine, msg]) => (
                      <li key={engine} style={{ overflowWrap: "anywhere" }}>
                        <strong>{displayModel(engine) || engine}:</strong> {msg}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {isComplete && (
                <RunSummarySection
                  summary={summary}
                  optimizations={optimizations}
                  statusFor={statusFor}
                  onAdd={handleAddOptimization}
                  onView={handleViewInFixActions}
                  onNavigate={handleNavigate}
                  gated={!ACTION_ITEMS_ENABLED}
                />
              )}

              <PipelineSection
                stages={stages}
                expandedStage={expandedStage}
                onToggleStage={toggleStage}
                completedStages={completedStages}
                currentLoadingStage={currentLoadingStage}
                liveLoadingMessage={stageLoadingMessage}
                selectedModelIds={selectedModelIds}
                onAsk={handleAsk}
              />

              {isComplete && (
                <GapsSection
                  rows={gaps.rows}
                  summary={gaps.summary}
                  expandSignal={gapsExpandNonce}
                  highlightAttribute={gapsHighlightAttr}
                />
              )}
            </div>
          ) : (
            <div style={{ padding: "26px clamp(16px, 5vw, 64px) 56px" }}>
              <HistorySection rows={historyRows} onSelectRun={handleLoadHistoryRun} isLoadingRun={runLoading} />
            </div>
          )}
        </div>

        <CognitionModelModal
          open={showModelModal}
          onOpenChange={setShowModelModal}
          aiModels={aiModels}
          selectedModels={selectedModels}
          onToggleModel={toggleModel}
          onUseRecommended={useRecommended}
          onSelectAll={selectAllModels}
          onClearAll={clearAllModels}
        />

        {showActionLock && (
          <LockedFeatureModal
            feature="action-items"
            title={ACTION_ITEMS_LOCK_COPY.title}
            desc={ACTION_ITEMS_LOCK_COPY.desc}
            highlights={ACTION_ITEMS_LOCK_COPY.highlights}
            ctaLabel={ACTION_ITEMS_LOCK_COPY.cta}
            toastMessage={ACTION_ITEMS_LOCK_COPY.toast}
            onClose={() => setShowActionLock(false)}
          />
        )}

        <div className="hidden lg:block">
          <AIAssistantSidebar
            phase="cognition"
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={onSidebarToggle}
            contextualUpdate={aiContextualUpdate}
            externalQuestion={externalQuestion}
          />
        </div>
      </div>
    </CognitionCtx.Provider>
  );
}
